// src/utils/sound.js
let notifAudio = null;
let permissionGranted = false;
let audioUnlocked = false;
let unlockAttempted = false;

const NOTIF_MP3 = '/assets/sounds/s2.wav';
let initialized = false;

/**
 * Initialise le système de notification + son
 */
export async function initSounds() {
  if (initialized) return; // ✅ Bloque le 2ème appel
  initialized = true;

  // 1️⃣ Demander la permission pour les notifications
  if ('Notification' in window) {
    const permission = await Notification.requestPermission();
    permissionGranted = permission === 'granted';
  }

  // 2️⃣ Préparer l'audio
  try {
    notifAudio = new Audio(NOTIF_MP3);
    notifAudio.preload = 'auto';
    notifAudio.volume = 0.6;
  } catch (err) {
    console.error(err);
  }

  // 3️⃣ Tentative de déverrouillage automatique
  await attemptAutoUnlock();

  // 4️⃣ Déverrouillage sur interaction utilisateur (backup)
  setupUnlockListeners();
}

/**
 * Tentative de déverrouillage automatique (silencieux)
 */
async function attemptAutoUnlock() {
  if (!notifAudio || audioUnlocked || unlockAttempted) return;
  
  unlockAttempted = true;

  try {
    // Créer un contexte audio vide et le démarrer
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Reprendre le contexte (nécessaire sur certains navigateurs)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    // Jouer silencieusement l'audio
    const originalVolume = notifAudio.volume;
    notifAudio.volume = 0; // Volume à 0 pour ne pas déranger
    
    const playPromise = notifAudio.play();
    
    if (playPromise !== undefined) {
      await playPromise;
      notifAudio.pause();
      notifAudio.currentTime = 0;
      notifAudio.volume = originalVolume; // Restaurer le volume
      audioUnlocked = true;
    }
  } catch (err) {
    // console.error(err);
  }
}

/**
 * Configuration des listeners pour déverrouillage manuel
 */
function setupUnlockListeners() {
  if (audioUnlocked) return;

  const unlock = async () => {
    if (notifAudio && !audioUnlocked) {
      try {
        const originalVolume = notifAudio.volume;
        notifAudio.volume = 0;
        
        const playPromise = notifAudio.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          notifAudio.pause();
          notifAudio.currentTime = 0;
          notifAudio.volume = originalVolume;
          audioUnlocked = true;
          
          removeUnlockListeners();
        }
      } catch (err) {
        // console.error(err);
      }
    }
  };

  const removeUnlockListeners = () => {
    window.removeEventListener('click', unlock, true);
    window.removeEventListener('touchstart', unlock, true);
    window.removeEventListener('keydown', unlock, true);
    window.removeEventListener('mousedown', unlock, true);
    window.removeEventListener('touchend', unlock, true);
  };

  // Attacher les listeners en phase capture
  window.addEventListener('click', unlock, true);
  window.addEventListener('touchstart', unlock, true);
  window.addEventListener('keydown', unlock, true);
  window.addEventListener('mousedown', unlock, true);
  window.addEventListener('touchend', unlock, true);

}

/**
 * Joue le son ET affiche une notification navigateur
 */
export function playNotif(title = 'Nouvelle notification', body = 'Vous avez reçu un nouveau message') {

  // 🔊 Méthode 1 : Via Notification du navigateur
  if (permissionGranted && 'Notification' in window) {
    try {
      const notification = new Notification(title, {
        body: body,
        icon: '/logo.png',
        badge: '/logo.png',
        tag: 'cliclaim-notif-' + Date.now(),
        requireInteraction: false,
        silent: false,
      });

      setTimeout(() => notification.close(), 5000);

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

    } catch (err) {
      console.error(err);
    }
  }

  // 🔊 Méthode 2 : Son personnalisé
  if (notifAudio) {
    notifAudio.currentTime = 0;
    
    notifAudio.play()
      .then(() => {
        
        // Si c'était le premier son réussi, marquer comme déverrouillé
        if (!audioUnlocked) {
          audioUnlocked = true;
        }
      })
      .catch((err) => {
        
        if (!audioUnlocked) {
          // Audio locked
        }
      });
  }
}

/**
 * Vérifier si les notifications sont supportées et autorisées
 */
export function isNotificationSupported() {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Demander la permission manuellement
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return false;
  }

  const permission = await Notification.requestPermission();
  permissionGranted = permission === 'granted';
  
  // Retenter le déverrouillage après permission
  if (permissionGranted && !audioUnlocked) {
    await attemptAutoUnlock();
  }
  
  return permissionGranted;
}

/**
 * Vérifier si l'audio est déverrouillé
 */
export function isAudioUnlocked() {
  return audioUnlocked;
}

/**
 * Force le déverrouillage manuel (appelé par exemple sur un bouton)
 */
export async function forceUnlockAudio() {
  if (audioUnlocked) {
    return true;
  }

  if (!notifAudio) {
    return false;
  }

  try {
    const originalVolume = notifAudio.volume;
    notifAudio.volume = 0;
    
    await notifAudio.play();
    notifAudio.pause();
    notifAudio.currentTime = 0;
    notifAudio.volume = originalVolume;
    
    audioUnlocked = true;
    return true;
  } catch (err) {
    return false;
  }
}
